import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../config";
import { useApp } from "../context/AppContext";
import "./Generar.css";

function getToken() {
  return localStorage.getItem("token") || "";
}

export default function Generar() {
  const navigate  = useNavigate();
  const { promptEng, setPromptEng, autoGenerate, setAutoGenerate, motor, setMotor, resumen, mdvObject, editImageId, setEditImageId } = useApp();

  const activePrompt = promptEng || "";

  const [editablePrompt, setEditablePrompt] = useState(activePrompt);
  const [generations, setGenerations] = useState([]);
  const [activeGenId, setActiveGenId] = useState(null);
  const [seed, setSeed]               = useState("");
  const framesRef                     = useRef(null);

  const activeGen = generations.find(g => g.id === activeGenId) || null;

  useEffect(() => {
    setEditablePrompt(promptEng || "");
  }, [promptEng]);

  useEffect(() => {
    if (!activeGenId || !framesRef.current) return;
    const el = framesRef.current.querySelector(`[data-id="${activeGenId}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", inline: "nearest", block: "nearest" });
  }, [activeGenId]);

  const pollJob = useCallback(async (localId, jobId) => {
    const maxPolls = 60;
    for (let i = 0; i < maxPolls; i++) {
      await new Promise(r => setTimeout(r, 3000));
      try {
        const res = await fetch(`${API_URL}/generate/${encodeURIComponent(jobId)}/status`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        const data = await res.json();
        if (data.status === "completed") {
          const resultRes = await fetch(`${API_URL}/generate/${encodeURIComponent(jobId)}/result`, {
            headers: { Authorization: `Bearer ${getToken()}` },
          });
          const result = await resultRes.json();
          setGenerations(prev => prev.map(g =>
            g.id === localId ? {
              ...g, status: "done",
              image: result.image, imageId: result.image_id,
              seed: result.seed, prompt: result.prompt,
              width: result.width, height: result.height,
            } : g
          ));
          return;
        }
        if (data.status === "error") {
          setGenerations(prev => prev.map(g =>
            g.id === localId ? { ...g, status: "error", error: data.error || "Error" } : g
          ));
          return;
        }
      } catch (e) { /* seguir */ }
    }
    setGenerations(prev => prev.map(g =>
      g.id === localId ? { ...g, status: "error", error: "Timeout" } : g
    ));
  }, []);

  const generate = useCallback(async () => {
    const prompt = editablePrompt.trim();
    if (!prompt) return;
    setPromptEng(prompt);
    setEditImageId(null);

    const id  = Date.now().toString();
    setGenerations(prev => [...prev, { id, status: "loading", image: null, imageId: null, promptEng: prompt }]);
    setActiveGenId(id);

    try {
      const res = await fetch(`${API_URL}/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ positive: prompt, seed: seed ? parseInt(seed, 10) : -1, motor, resumen, mdv_json: mdvObject ? JSON.stringify(mdvObject) : "" }),
      });
      const data = await res.json();
      if (data.job_id) {
        pollJob(id, data.job_id);
      } else if (data.image) {
        setGenerations(prev => prev.map(g =>
          g.id === id ? { ...g, status: "done", image: data.image, imageId: data.image_id, seed: data.seed, width: data.width, height: data.height } : g
        ));
      } else {
        throw new Error(data.detail || "Error");
      }
    } catch (e) {
      setGenerations(prev => prev.map(g =>
        g.id === id ? { ...g, status: "error", error: e.message } : g
      ));
    }
  }, [editablePrompt, seed, pollJob, setPromptEng, setEditImageId, motor, resumen, mdvObject]);

  // Auto-generate when coming from "Generar Imagen" action tag
  const autoGenRef = useRef(false);
  useEffect(() => {
    if (autoGenerate && editablePrompt.trim() && !autoGenRef.current) {
      autoGenRef.current = true;
      setAutoGenerate(false);
      generate();
    }
  }, [autoGenerate, editablePrompt, generate, setAutoGenerate]);

  // Load image from Biblioteca edit (re-fetches each time component mounts if editImageId is set)
  useEffect(() => {
    if (editImageId && generations.length === 0) {
      const imgId = editImageId;
      fetch(`${API_URL}/images/${encodeURIComponent(imgId)}/file`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
        .then(res => res.blob())
        .then(blob => {
          const reader = new FileReader();
          reader.onload = () => {
            const b64 = reader.result.split(",")[1];
            const id = Date.now().toString();
            setGenerations(prev => [...prev, { id, status: "done", image: b64, imageId: imgId }]);
            setActiveGenId(id);
          };
          reader.readAsDataURL(blob);
        })
        .catch(() => {});
    }
  }, []); // eslint-disable-line

  const download = (gen) => {
    if (!gen?.image) return;
    const link = document.createElement("a");
    link.href = `data:image/png;base64,${gen.image}`;
    link.download = `keyframe_${gen.imageId || "output"}.png`;
    link.click();
  };

  const shortId = (gen) => gen?.imageId ? gen.imageId.replace("#720-", "").slice(-6) : "";

  const scrollCarrete = (dir) => {
    if (framesRef.current) framesRef.current.scrollBy({ left: dir * 200, behavior: "smooth" });
  };

  if (!activePrompt && !editablePrompt) {
    return (
      <div className="generar-page">
        <div className="generar-empty-state">
          <div className="generar-empty-icon">▶</div>
          <h3>Sin prompt activo</h3>
          <p>Genera un Super Prompt en Prompt IN para poder generar imágenes.</p>
          <button className="generar-btn-go" onClick={() => navigate("/keyframes/prompt-in")}>
            → Ir a Prompt IN
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="generar-page">

      {/* ── Imagen (izquierda, cuadrada) ── */}
      <div className="generar-image-area">
        {!activeGen && (
          <div className="generar-empty-img">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="M21 15l-5-5L5 21" />
            </svg>
            <h3>Sin imagen</h3>
            <p>Pulsa Generar Imagen</p>
          </div>
        )}

        {activeGen?.status === "loading" && (
          <div className="generar-loading">
            <div className="generar-loading-dot" />
            <p>Generando · puede tardar unos segundos</p>
          </div>
        )}

        {activeGen?.status === "error" && (
          <div className="generar-error-msg">
            <h3>Error</h3>
            <p>{activeGen.error}</p>
          </div>
        )}

        {activeGen?.status === "done" && activeGen.image && (
          <img src={`data:image/png;base64,${activeGen.image}`} alt="Key frame" />
        )}

        {activeGen?.status === "done" && (
          <div className="generar-meta-overlay">
            <span className="generar-meta-id">{activeGen.imageId}</span>
            <span className="generar-meta-info">
              {activeGen.width}×{activeGen.height} · seed {activeGen.seed}
            </span>
            <div className="generar-meta-actions">
              <button className="generar-meta-btn" onClick={() => download(activeGen)}>↓ PNG</button>
              <button className="generar-meta-btn" onClick={() => navigate("/biblioteca")}>Biblioteca</button>
            </div>
          </div>
        )}
      </div>

      {/* ── Panel derecho: prompt + botones + carrete ── */}
      <div className="generar-right">

        {/* Prompt editable */}
        <div className="prompter-section">
          <div className="prompter-label">
            PROMPTER
            <button
              className="prompter-copy-btn"
              onClick={() => { navigator.clipboard.writeText(editablePrompt); }}
              title="Copiar prompt"
            >⧉</button>
            <button className="prompter-nav-btn" onClick={() => navigate("/keyframes/prompt-in")}>
              Prompt IN
            </button>
            <button className="prompter-nav-btn" onClick={() => navigate(-1)}>
              ← Volver
            </button>
          </div>
          <textarea
            className="prompter-textarea"
            value={editablePrompt}
            onChange={e => setEditablePrompt(e.target.value)}
            placeholder="Super Prompt editable..."
          />
        </div>

        {/* Botones en fila */}
        <div className="prompter-buttons">
          <button className="prompter-btn prompter-btn--generate" onClick={generate} disabled={!editablePrompt.trim()}>
            Generar Imagen
          </button>
          <select
            className="prompter-btn prompter-btn--motor"
            value={motor}
            onChange={e => setMotor(e.target.value)}
          >
            <option value="sdxl">Juggernaut Hyper</option>
            <option value="ragnarok">Juggernaut Ragnarok</option>
            <option value="sdxl_base">SDXL 1.0 Base</option>
            <option value="flux2">Flux 2 Dev</option>
          </select>
          <button className="prompter-btn prompter-btn--seed" onClick={() => setSeed("")}>
            Seed: {seed || "Aleatorio"}
          </button>
        </div>

        {/* Carrete */}
        <div className="prompter-carrete">
          <button className="carrete-scroll-btn" onClick={() => scrollCarrete(-1)}>◀</button>
          <div className="carrete-frames" ref={framesRef}>
            {generations.map(gen => (
              <div
                key={gen.id}
                data-id={gen.id}
                className={`film-frame ${gen.id === activeGenId ? "active-f" : ""}`}
                onClick={() => gen.status === "done" && setActiveGenId(gen.id)}
              >
                {gen.status === "done" && gen.image && <img src={`data:image/png;base64,${gen.image}`} alt="" />}
                {gen.status === "loading" && <div className="generar-loading-dot" style={{width:8,height:8}} />}
                {gen.imageId && <div className="film-frame-id">{shortId(gen)}</div>}
              </div>
            ))}
          </div>
          <button className="carrete-scroll-btn" onClick={() => scrollCarrete(1)}>▶</button>
        </div>

      </div>
    </div>
  );
}
